import React, { FC, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  createStyles,
  List,
  ListSubheader,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { useQuery } from '@apollo/react-hooks';
import { GET_ENSEIGNES_WITH_ENTITIES } from '../../../../graphql/enseigne/query';
import CustomSelect, {
  OptionType,
} from '../../../../common/components/CustomSelect/CustomSelect';
import { GET_ALL_ENTITIES_FOR_USERS } from '../../../../graphql/entity/query';
import CheckBoxListItem from '../../ManageEntities/List/CheckBoxListItem/CheckBoxListItem';
import { groupByEchelon } from '../../../../utils/entity';
import { capitalizeFirstLetter } from '../../../../utils/common';
import { IEntity, IUserEditState } from '../utils';
import ConfirmationButtons from '../../../ConfirmationButtons/ConfirmationButtons';
import {
  EnseignesWithEntities,
  EnseignesWithEntitiesVariables,
} from '../../../../graphql/enseigne/types/EnseignesWithEntities';
import { SortOrder } from '../../../../types/graphql-global-types';
import {
  AllEntitiesForUsers,
  AllEntitiesForUsersVariables,
} from '../../../../graphql/entity/types/AllEntitiesForUsers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      textAlign: 'center',
      color: '#1A171B',
      fontSize: 20,
      fontWeight: 'bold',
      marginBottom: 5,
    },
    subtitle: {
      textAlign: 'center',
      color: '#9EA3B0',
      fontSize: 16,
      fontStyle: 'italic',
      padding: 30,
    },
    errorText: {
      color: 'red',
      paddingTop: 16,
      paddingBottom: 16,
    },
    list: {
      width: '100%',
    },
    listSubHeader: {
      height: 15,
      color: 'black',
      paddingLeft: 0,
    },
  }),
);

interface Params {
  userState: IUserEditState;
  setUserState: React.Dispatch<React.SetStateAction<IUserEditState>>;
  setIsEditingEntities(isEditing: boolean): void;
}

const EditUserEntities: FC<Params> = (props) => {
  const { userState, setUserState, setIsEditingEntities } = props;
  const classes = useStyles();

  const [selectedEnseigneId, setSelectedEnseigneId] = useState<number>(
    userState.userEnseigne ? userState.userEnseigne.id : 0,
  );

  const [selectedEntityIds, setSelectedEntityIds] = useState<number[]>(
    userState.entities ? userState.entities.map((entity) => entity.id) : [],
  );

  const { data, loading } = useQuery<
    EnseignesWithEntities,
    EnseignesWithEntitiesVariables
  >(GET_ENSEIGNES_WITH_ENTITIES, {
    variables: { input: { orderBy: { name: SortOrder.asc } } },
  });

  const { data: entities, loading: loadingEntities } = useQuery<
    AllEntitiesForUsers,
    AllEntitiesForUsersVariables
  >(GET_ALL_ENTITIES_FOR_USERS, {
    variables: {
      input: {
        where: { enseignes: [{ idCC: selectedEnseigneId }] },
        orderBy: { name: SortOrder.asc },
      },
    },
  });

  const allEnseignes: OptionType[] =
    data?.allEnseignes?.enseignes?.map((enseigne) => {
      return { value: enseigne?.id ?? -1, label: enseigne?.name ?? '' };
    }) ?? [];

  const allEntitiesByEchelon = useMemo(
    () => groupByEchelon(entities?.allEntities?.entities as Partial<IEntity>[]),
    [entities],
  );

  const handleChangeEnseigne = (
    event: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>,
  ) => {
    const value = event.target.value;
    setSelectedEnseigneId(value as number);
    setSelectedEntityIds([]);
  };

  const handleChangeEntities = (
    event: React.ChangeEvent<HTMLInputElement>,
    entityId: number,
  ) => {
    if (event.target.checked) {
      if (!selectedEntityIds.includes(entityId)) {
        setSelectedEntityIds(selectedEntityIds.concat([entityId]));
      }
    } else {
      setSelectedEntityIds(selectedEntityIds.filter((id) => id !== entityId));
    }
  };

  const handleConfirm = () => {
    if (entities?.allEntities?.entities?.length) {
      const selectedEntities = entities?.allEntities?.entities
        ?.filter(
          (entity) => !!entity?.id && selectedEntityIds.includes(entity?.id),
        )
        .map((entity) => ({
          id: entity?.id as number,
          name: entity?.name || '',
        }));

      setUserState((prev) => {
        const nextState = {
          ...prev,
          entities: selectedEntities,
        };

        return nextState;
      });
    }

    if (data?.allEnseignes?.enseignes?.length) {
      const selectedEnseigne = data?.allEnseignes?.enseignes?.find(
        (enseigne) => enseigne?.id === selectedEnseigneId,
      );
      if (selectedEnseigne) {
        setUserState((prev) => {
          return {
            ...prev,
            userEnseigne: {
              id: selectedEnseigne.id,
              name: selectedEnseigne.name,
              entities: selectedEnseigne.entities?.map((entity) => ({
                id: entity.id,
                name: entity.name || '',
                managed:
                  prev.userEnseigne?.id === selectedEnseigne.id
                    ? prev.userEnseigne.entities?.find(
                        (currentEntity) => currentEntity.id === entity.id,
                      )?.managed
                    : false,
                echelon: {
                  ...entity.echelon,
                },
              })),
            },
          };
        });
      }
    }

    setIsEditingEntities(false);
  };

  const handleCancel = () => {
    setIsEditingEntities(false);
  };

  if (!loading && !allEnseignes?.length)
    return (
      <Box className={classes.subtitle}>
        Aucune enseigne trouvé dans la base de données.
      </Box>
    );

  return (
    <>
      <CustomSelect
        label="Enseigne"
        value={selectedEnseigneId}
        options={allEnseignes}
        onChange={handleChangeEnseigne}
        loading={loading}
      />

      {loadingEntities ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={100}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        allEntitiesByEchelon?.map((_echelon: any, index: any) => (
          <List
            key={`echelon-list-${index}`}
            subheader={
              <ListSubheader component="div" className={classes.listSubHeader}>
                {`${capitalizeFirstLetter(_echelon?.name)} :`}
              </ListSubheader>
            }
            className={classes.list}>
            {_echelon?.entities?.map((entity: any) => (
              <CheckBoxListItem
                key={`entity-list-${entity.id}`}
                id={entity.id}
                checked={selectedEntityIds.includes(entity.id)}
                text={entity.name || ''}
                handleChange={handleChangeEntities}
                disabled={false}
              />
            ))}
          </List>
        ))
      )}

      <Box className={classes.errorText}>
        {!!selectedEnseigneId &&
          !allEntitiesByEchelon?.length &&
          "Aucune entité trouvée dans l'enseigne sélectionnée."}
        {!selectedEnseigneId &&
          'Veuillez sélectionner une enseigne pour sauvegarder.'}
      </Box>

      <ConfirmationButtons
        handleCancel={handleCancel}
        handleConfirm={handleConfirm}
        disableConfirm={!selectedEnseigneId}
      />
    </>
  );
};

export default EditUserEntities;
